/**
 * `statusCatalog` — a `name → one-line RAW blurb` lookup for status/condition chips, plus the
 * `expiryLabel` helper the chip tooltip badges an instance's duration with. Presentation only:
 * the blurb is the human-readable description surfaced in the brutalist status tooltip
 * ({@link ../StatusChip StatusTipBody}); no status semantics live here — see the `*_STATUS`
 * constants (`packages/domain/src/encounter/types.ts`, `.../movement.ts`) and the overlay seed
 * entries (`packages/rules/src/encounter/effects/seedEntries.ts`) for the real mechanics.
 *
 * **Full-coverage**: every status/condition the engine can stamp has a blurb — the RAW 8 statuses
 * + 7 conditions + the non-core buffs (Bolster/Braced) and the engine-invented composites/markers
 * (Grappled, Cascading, COMP/CON Engaged, Unpiloted, the Jockey/Ride pairs,
 * Dead/Vaporized/Destroyed, the Open door marker). `statusCatalog.test.ts` pins that coverage
 * against the two authoritative vocabularies (the overlay `SEED_ENTRIES` + the exported domain
 * `*_STATUS` constants), so a newly-seeded status fails until it gets a line here. Wording is
 * sourced from printed RAW / the seed-entry + constant doc comments. The lookup degrades to
 * name-only for anything not listed (e.g. a homebrew free-text status), never a crash.
 */

import type { StatusExpiry, StatusExpiryWho } from "@lancer-vtt/domain";

/** `StatusInstance.name` → a single-line RAW-sourced blurb. */
export const STATUS_BLURBS: Readonly<Record<string, string>> = {
  // ── RAW statuses (Core p.77) ──
  Engaged: "adjacent to a hostile; ranged attacks against non-adjacent targets are harder",
  Disengaged: "ignoring engagement while moving this turn",
  // **ABOVE, not below** — Core p.62: "Characters are in the DANGER ZONE when half or more of their
  // heat is filled in", and the tag glossary (p.104) prices it as "heat equal to at least half of
  // their HEAT CAP". Our own gate agrees: `predicate.ts`'s `danger_zone` arm is `heat >= maxHeat/2`.
  // This line read "at or below" until 2026-08-31 — the direction inverted, so it told a GM a cool
  // mech was the smoking one. Copied in from `seedEntries.ts`'s DANGER_ZONE doc comment (this file's
  // header says that is where wording comes from), which carried the same inversion while the entry's
  // OWN advisory string beside it said "≥ half HEAT CAP"; both are corrected.
  "Danger Zone": "at or above half of maximum heat capacity; some systems and talents require it",
  "Down and Out": "unconscious and Stunned; any further damage kills",
  Exposed: "kinetic, explosive, and energy damage taken is doubled",
  Hidden: "can't be targeted by hostile attacks/actions; enemies know only your approximate location",
  Blinded: "line of sight only to adjacent spaces: can't attack, scan or target anything farther away",
  "Soft Cover": "ranged attacks against you are made at +1 difficulty; doesn't stack with other cover",
  Invisible: "attacks against you have a 50% chance to miss outright; can always Hide, even without cover",
  Prone: "attacks against you get +1 accuracy; you're Slowed and count as moving in difficult terrain",
  "Shut Down": "stunned indefinitely; nothing can prevent or end this but powering back on",
  // ── RAW conditions (Core p.78) ──
  Immobilized: "cannot make any voluntary movement (involuntary movement unaffected)",
  Impaired: "+1 difficulty on all attacks, saves, and skill checks",
  Jammed: "can't use comms, take reactions, or take/benefit from tech actions; attacks limited to Improvised/Grapple/Ram",
  "Lock On": "an attacker may consume this for +1 accuracy against you, then it clears",
  Shredded: "gains no benefit from armor or resistance",
  Slowed: "can only take a standard move; no Boost or movement-granting talents/systems/weapons",
  Stunned: "evasion capped at 5; auto-fails HULL and AGILITY checks and saves; can't move, act, or overcharge",
  // ── non-core buffs/markers with RAW effects ──
  Bolster: "+2 accuracy to your next save or skill check, then it's spent",
  Braced: "attacks against you are made at +1 difficulty until the end of your next turn; no reactions until then, and on that turn only one quick action (no move, Overcharge, full or free actions)",
  Flying: "airborne; passes over occupants and terrain, ignores difficult terrain, and can't be knocked Prone",
  Grappled: "mutually bound; no Boost or reactions; the smaller combatant is Immobilized and dragged along",
  // Core, GRAPPLE: "If both parties are the same SIZE, either can make contested HULL checks at the start
  // of their turn: the winner counts as larger than the loser until this contest is repeated."
  "Grapple Dominance": "won the contested HULL check in a same-Size grapple: counts as larger, so the partner is Immobilized and dragged along, until the contest is repeated",
  Intangible: "ignores movement obstructions and can't be targeted by most attacks",
  "Argonaut Shield": "shielded by an adjacent Drake: Resistance to all damage, and the Drake takes half the damage you would take; ends when you break adjacency",
  "Covering Fire Mark": "Impaired by Covering Fire; moving more than 1 space clears Impaired, but the gunner may take a reaction shot at you with a Heavy ranged weapon",
  Reeling: "ambushed: unable to take reactions until the end of your next turn",
  Prowling: "on your turn: being seen or leaving cover doesn't break Hidden, you can Hide in plain sight, and you can pass through (not stop in) hostile spaces",
  Terraforming: "bulldozes on its move: drives through terrain, walls and occupants, running over anything in its path",
  Trampling: "ignores engagement and passes through (but can't stop in) other characters' spaces, damaging them",
  Feinting: "ignores engagement while moving, and the feinted character can't react to your movement",
  Tripping: "can pass through (but not stop in) the tripped character's space this turn",
  Biological: "a living NPC; no heat track, and immune to tech actions other than Scan and Lock On",
  // ── NPC-feature marks (the pack's own statuses, `packs/core-rulebook/entries/statuses/`) ──
  // Sniper p.309: "Characters with the SNIPER'S MARK … ignore it and take damage normally if they
  // are in cover or PRONE. On their turn, characters with the SNIPER'S MARK may drop PRONE as a free
  // action." The mark never expires on its own; it ends only when the Sniper transfers it.
  "Sniper's Mark": "the Sniper's Anti-Materiel Rifle deals 1 structure instead of damage to you, unless you're in cover or Prone; on your turn you may drop Prone as a free action; it lasts until the Sniper marks someone else",
  // Goliath p.300: "+3 difficulty to attack against any character other than the Goliath until the
  // start of the Goliath's next turn" — and it ends early if that Goliath is destroyed or removed.
  "Crush Targeting Target": "+3 difficulty on attacks against anyone other than the Goliath that marked you, until the start of that Goliath's next turn",
  // ── SAFE_CRACKER's invade options (Hacker rank 2, `t_hacker:2`) ──
  "Jam Cockpit": "no one can Mount or Dismount this mech (Eject still works) until it's fixed with an ENGINEERING check as a full action",
  "Disable Life Support": "+1 Difficulty on all saves (not checks) until life support is rebooted with a SYSTEMS check as a quick action",
  "Hack/Slash": "can't take or benefit from Quick or Full Tech actions until you reboot (a SYSTEMS check as a quick action) or shut down",
  // ── Neurospike's and the tracking bug's statuses ──
  "Shrike Code": "take 2 heat whenever you attack, before the roll, until the end of your next turn",
  Mirage: "the mech that was invaded treats you as Invisible (a 50% miss chance on its attacks against you) until the end of its next turn",
  "Tracking Bug": "the mech that planted it knows where you are and ignores your Invisible; you can't Hide; remove it with an ENGINEERING check as a quick action, or it ends with the scene",
  // ── Viral Logic Suite's statuses ──
  Banish: "take 2 heat for every space you choose to move, up to 6 heat in all, until the end of your next turn",
  "Logic Bomb": "you stay Slowed until you end a turn with no character adjacent to you",
  // ── engine-invented composites/markers (no lancer-data entry) ──
  Cascading: "the NHP has taken over the mech, now acting as an NPC under GM control",
  // Core p.106: "AIs don't benefit from the pilot's talents or other features"; control returns at the start of a later turn.
  "COMP/CON Engaged": "the mech's COMP/CON is in control: you drive its actions as usual, but your talents and core bonuses don't apply; take control back at the start of a later turn",
  Unpiloted: "no pilot aboard; can't act or be activated (but not powered down)",
  Jockeying: "clinging onto a hostile mech to Jockey it; shares its space and can jump off while moving",
  Jockeyed: "a hostile pilot is clinging on and Jockeying this mech",
  Riding: "riding a willing allied mech or vehicle; shares its space and moves along with it",
  Ridden: "an allied pilot is riding along on this mech",
  Dead: "the pilot took further damage while Down and Out and is dead",
  Vaporized: "destroyed outright, leaving no wreck behind",
  Destroyed: "reduced to 0 and removed from the fight",
  Open: "this door is open; units can move through its cell",
} as const;

/** The blurb for a status name, or `undefined` if none is catalogued — never throws. */
export function statusBlurb(name: string): string | undefined {
  return STATUS_BLURBS[name];
}

/**
 * Human-readable duration labels for the chip tooltip's expiry badge, keyed by the
 * `StatusExpiry.kind` — the `turn`-kind labels are built from its `edge`/`who` axes in
 * {@link expiryLabel} below rather than listed flat here.
 */
const EXPIRY_LABELS: Readonly<Record<string, string>> = {
  save_ends: "save ends",
  end_of_round: "until end of round",
  scene: "this scene",
  movement: "until this move ends",
  this_turn: "until this turn ends",
} as const;

/** The `turn`-kind expiry's `who`/`edge` axes → its badge phrasing. */
const TURN_EXPIRY_LABELS: Readonly<Record<StatusExpiryWho, Readonly<Record<"start" | "end", string>>>> = {
  target: { end: "until end of its next turn", start: "until start of its next turn" },
  source: { end: "until source's next turn ends", start: "until source's next turn starts" },
  partner: { end: "until partner's next turn ends", start: "until partner's next turn starts" },
};

/**
 * A short human label for a status instance's `expiry`, for the tooltip's duration badge — or
 * `undefined` for the un-informative default `until_removed` (a manual/positional status that
 * persists until cleared) so the badge is simply omitted.
 */
export function expiryLabel(expiry: StatusExpiry): string | undefined {
  if (expiry.kind === "until_removed") return undefined;
  if (expiry.kind === "turn") return TURN_EXPIRY_LABELS[expiry.who][expiry.edge];
  if (expiry.kind === "while_status") return `while ${expiry.status} holds`;
  return EXPIRY_LABELS[expiry.kind] ?? expiry.kind;
}
